import { humanizeEnum } from './labels';

/**
 * Who an email goes to, in one table.
 *
 * Two stored fields answer the same question and used to be labelled separately, each with a
 * two-way ternary that sent anything new to "Employers" or to "-":
 *
 * - `email_automation_rules.audience`: JOBSEEKERS, EMPLOYERS, ALL (content announcements to
 *   everyone), SUBSCRIBERS (guide subscribers), INTERNAL (a staff inbox, e.g. int-1).
 * - `email_send_records.userType`: JOBSEEKER, EMPLOYER, HR, INTERNAL (staff, userless,
 *   untracked), PROSPECT (a person with no account, tracked), TEST (a template test send,
 *   excluded from analytics totals by the backend). Account types arrive in both casings: lower
 *   case from direct sends, upper case from the rule engine. The backend filters this field
 *   case-insensitively, so one upper-case filter value catches both, and the lookup here is
 *   case-insensitive too.
 *
 * Anything not in the table is humanised ("NEW_THING" -> "New thing") rather than mapped to a
 * neighbour or blanked: an unrecognised audience on screen is a question, a wrong one is a lie.
 *
 * Display only. Filter on the stored value, never on the label.
 */
export const EMAIL_AUDIENCE_LABELS: Record<string, string> = {
  JOBSEEKER: 'Jobseekers',
  JOBSEEKERS: 'Jobseekers',
  EMPLOYER: 'Employers',
  EMPLOYERS: 'Employers',
  HR: 'HR team',
  ALL: 'Jobseekers & Employers',
  SUBSCRIBERS: 'Guide subscribers',
  INTERNAL: 'Internal staff',
  PROSPECT: 'No account',
  TEST: 'Test send',
};

/** Shown when the field is missing altogether: there is no value to humanise. */
export const AUDIENCE_NOT_RECORDED = 'Not recorded';

export const emailAudienceLabel = (value?: string | null): string => {
  const raw = (value ?? '').trim();
  if (!raw) return AUDIENCE_NOT_RECORDED;
  return EMAIL_AUDIENCE_LABELS[raw.toUpperCase()] ?? humanizeEnum(raw, AUDIENCE_NOT_RECORDED);
};

export const INTERNAL_AUDIENCE = EMAIL_AUDIENCE_LABELS.INTERNAL;

/** Stored rule audiences, in the order the Automation rules filter offers them. */
export const RULE_AUDIENCES = ['JOBSEEKERS', 'EMPLOYERS', 'ALL', 'SUBSCRIBERS', 'INTERNAL'];

/** Stored send-record user types, in the order the Email audit filter offers them. */
export const SEND_USER_TYPES = ['JOBSEEKER', 'EMPLOYER', 'HR', 'INTERNAL', 'PROSPECT', 'TEST'];

/**
 * Filter options for the Automation rules tab. Rows there carry the label, so the option values
 * are labels; any audience the rules actually carry that the table does not know is added, so a
 * new audience is filterable the day it appears rather than reachable only through "All".
 */
export const ruleAudienceOptions = (present: string[] = []) => {
  const labels = RULE_AUDIENCES.map((a) => emailAudienceLabel(a));
  present.forEach((label) => {
    if (label && !labels.includes(label)) labels.push(label);
  });
  return labels.map((label) => ({ value: label, label }));
};

/** Filter options for the Email audit page: the stored value, since the server filters on it. */
export const SEND_USER_TYPE_OPTIONS = [
  { value: '', label: 'All user types' },
  ...SEND_USER_TYPES.map((t) => ({ value: t, label: emailAudienceLabel(t) })),
];
